import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, Clock, Download, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import { MobileTopCenter } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { PageHeader } from '../components/ui/page-header'
import { api, type DocAuditEntry, type DocSignature } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/$id',
  component: DocumentDetailPage,
})

function DocumentDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: usersData } = useQuery({
    queryKey: ['documents', 'sendable-users'],
    queryFn: () => api.documentSendableUsers(),
    enabled: !!meData?.user,
  })
  const { data, isLoading, error } = useQuery({
    queryKey: ['documents', id],
    queryFn: () => api.documentDetail(id),
    enabled: !!meData?.user,
  })

  const mailSource = useQuery({
    queryKey: ['documents', id, 'mail-source', meData?.user.id],
    queryFn: () => api.documentMailSource(id),
    enabled: !!meData?.user && !!data,
    retry: false,
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  if (isLoading) return <p className="text-text-tertiary">Lade …</p>
  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link
          to="/documents"
          search={{ tab: 'mailbox' }}
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück
        </Link>
        <p className="text-danger">Dokument nicht gefunden oder kein Zugriff.</p>
      </div>
    )
  }

  const myId = meData.user.id
  const { document: doc, signatures, auditLog } = data
  const userById = new Map([
    ...(usersData?.users.map((u) => [u.id, u] as const) ?? []),
    [myId, { id: myId, name: meData.user.name, email: meData.user.email, role: meData.user.role }],
  ])
  const mySig = signatures.find((s) => s.recipientUserId === myId)
  const isUploader = doc.uploaderUserId === myId

  return (
    <div className="space-y-6 animate-fade-in">
      {mailSource.data && (
        <a
          className="inline-flex min-h-11 items-center text-sm text-accent"
          href={`${import.meta.env.BASE_URL}mail?push_account=${mailSource.data.accountId}&push_message=${mailSource.data.messageId}`}
        >
          Zur ursprünglichen Mail
        </a>
      )}
      {/* Mobile-Shell (<640): ←+Dokumenttitel in der Top-Mitte, Minimal-Bar
          unten (Signieren/Senden bleibt im Content, Mapping §3). Der ←
          nimmt dasselbe Ziel wie der In-Content-Zurück-Link. */}
      <MobileTopCenter>
        <MobileBackTitle
          title={doc.filename}
          onBack={() =>
            navigate({
              to: '/documents',
              search: { tab: mySig ? ('mailbox' as const) : ('files' as const) },
            })
          }
          backAriaLabel="Zurück zu den Dateien"
        />
      </MobileTopCenter>

      <div>
        {/* Einreihiger Standard-Kopf statt Serif-Display (Ticket „obere
            Leiste einheitlich"): Zurück-Link + Titel in EINER Zeile; die
            Meta-Zeile bleibt auch mobil sichtbar, Titel <640 in der
            Shell-Top-Leiste (keep-row). */}
        <PageHeader
          title={doc.filename}
          phoneMode="keep-row"
          className="border-b-0 pb-0"
          inline={
            <Link
              to="/documents"
              search={{ tab: mySig ? ('mailbox' as const) : ('files' as const) }}
              className="phone:hidden order-first inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary shrink-0"
            >
              <ArrowLeft className="h-4 w-4" /> Zurück
            </Link>
          }
        />
        <p className="mt-1 text-xs text-text-tertiary">
          {doc.pageCount ? `${doc.pageCount} Seiten · ` : ''}
          {Math.round(doc.sizeBytes / 1024)} KB · hochgeladen{' '}
          {new Date(doc.createdAt).toLocaleString('de-DE')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div className="card-elevated overflow-hidden">
          {doc.mimeType === 'application/pdf' ? (
            <iframe
              title={doc.filename}
              src={api.documentFileUrl(doc.id)}
              className="w-full h-[70vh] bg-white"
            />
          ) : ['image/png', 'image/jpeg'].includes(doc.mimeType) ? (
            <img
              src={api.chatAttachmentRawUrl(doc.id)}
              alt={doc.filename}
              className="w-full max-h-[70vh] object-contain"
            />
          ) : (
            <p className="p-4 text-sm text-text-secondary">
              Originaldatei zum Herunterladen. Keine aktive Vorschau.
            </p>
          )}
          <div className="px-4 py-3 border-t border-border-subtle flex items-center justify-between">
            <p className="text-xs text-text-tertiary">Originaldatei</p>
            <a
              href={
                doc.mimeType === 'application/pdf'
                  ? api.documentFileUrl(doc.id)
                  : api.chatAttachmentRawUrl(doc.id)
              }
              download={doc.filename}
              className="inline-flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </a>
          </div>
        </div>

        <div className="space-y-6">
          {mySig && mySig.status === 'pending' && (
            <SignPanel
              signatureId={mySig.id}
              onSigned={() => {
                queryClient.invalidateQueries({ queryKey: ['documents'] })
              }}
            />
          )}

          <div className="card-elevated p-5">
            <h2 className="text-base font-medium mb-4">Empfänger ({signatures.length})</h2>
            {signatures.length === 0 ? (
              <p className="text-sm text-text-tertiary">Noch nicht verschickt.</p>
            ) : (
              <ul className="space-y-2">
                {signatures.map((s) => {
                  const user = userById.get(s.recipientUserId)
                  return (
                    <li key={s.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-text-primary truncate">
                        {user?.name ?? s.recipientUserId}
                      </span>
                      <SigStatusBadge sig={s} />
                    </li>
                  )
                })}
              </ul>
            )}
            {isUploader && (
              <p className="mt-4 text-xs text-text-tertiary">
                Verschickt am{' '}
                {signatures[0] ? new Date(signatures[0].sentAt).toLocaleString('de-DE') : '—'}
              </p>
            )}
          </div>

          <AuditLogPanel entries={auditLog} userById={userById} signatures={signatures} doc={doc} />
        </div>
      </div>
    </div>
  )
}

function SigStatusBadge({ sig }: { sig: DocSignature }) {
  if (sig.status === 'signed') {
    return (
      <a
        href={api.documentSignedFileUrl(sig.documentId, sig.id)}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border bg-success/15 text-success border-success/20 hover:bg-success/20"
        title={
          sig.signedAt
            ? `Unterzeichnet ${new Date(sig.signedAt).toLocaleString('de-DE')}`
            : 'Unterzeichnet'
        }
      >
        <CheckCircle2 className="h-3 w-3" /> Signiert
      </a>
    )
  }
  if (sig.status === 'rejected') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border bg-danger/15 text-danger border-danger/20"
        title={sig.rejectionReason ?? undefined}
      >
        <XCircle className="h-3 w-3" /> Abgelehnt
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-2xs uppercase tracking-wide rounded-md border bg-warning/15 text-warning border-warning/20">
      <Clock className="h-3 w-3" /> Offen
    </span>
  )
}

function SignPanel({ signatureId, onSigned }: { signatureId: string; onSigned: () => void }) {
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(today)
  const [location, setLocation] = useState('')
  const [busy, setBusy] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)

  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, c.width, c.height)
    ctx.strokeStyle = '#0a0a0b'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current
    if (!c) return { x: 0, y: 0 }
    const r = c.getBoundingClientRect()
    return {
      x: ((e.clientX - r.left) / r.width) * c.width,
      y: ((e.clientY - r.top) / r.height) * c.height,
    }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    drawingRef.current = true
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const p = getPos(e)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
  }
  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const p = getPos(e)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
  }
  const handlePointerUp = () => {
    drawingRef.current = false
  }

  const clearCanvas = () => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, c.width, c.height)
  }

  const submit = async () => {
    const c = canvasRef.current
    if (!c) return
    const dataUrl = c.toDataURL('image/png')
    setBusy(true)
    try {
      await api.signDocument(signatureId, {
        date,
        location: location.trim(),
        signatureImageBase64: dataUrl,
      })
      notify.success('Unterzeichnet — signed copy erstellt.')
      onSigned()
    } catch (err) {
      notify.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    const ok = await confirm({
      title: 'Dokument ablehnen?',
      description: 'Der Sender wird informiert.',
      confirmLabel: 'Ablehnen',
      variant: 'danger',
    })
    if (!ok) return
    setBusy(true)
    try {
      await api.rejectDocument(signatureId, null)
      notify.success('Abgelehnt')
      onSigned()
    } catch (err) {
      notify.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card-elevated p-5 space-y-4">
      <h2 className="text-base font-medium">Unterzeichnen</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-text-tertiary font-medium">Datum</p>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-text-tertiary font-medium">Ort</p>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="z.B. Berlin"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs uppercase tracking-wide text-text-tertiary font-medium">
            Unterschrift
          </p>
          <button
            type="button"
            onClick={clearCanvas}
            className="text-xs text-text-tertiary hover:text-text-primary"
          >
            Löschen
          </button>
        </div>
        <canvas
          ref={canvasRef}
          width={500}
          height={140}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="w-full h-32 rounded-md border border-border-subtle bg-white touch-none cursor-crosshair"
        />
      </div>
      <div className="flex items-center justify-between pt-2">
        <Button variant="ghost" onClick={reject} disabled={busy}>
          Ablehnen
        </Button>
        <Button variant="primary" onClick={submit} disabled={busy || !location.trim()}>
          <CheckCircle2 className="h-4 w-4" /> Unterzeichnen
        </Button>
      </div>
    </div>
  )
}

function AuditLogPanel({
  entries,
  userById,
  signatures,
  doc,
}: {
  entries: DocAuditEntry[]
  userById: Map<string, { name: string; email: string; role?: string }>
  signatures: DocSignature[]
  doc: { uploaderUserId: string }
}) {
  const sigById = new Map(signatures.map((s) => [s.id, s]))
  return (
    <div className="card-elevated p-5">
      <h2 className="text-base font-medium mb-4">Verlauf</h2>
      {entries.length === 0 ? (
        <p className="text-sm text-text-tertiary">Noch keine Einträge.</p>
      ) : (
        <ol className="space-y-3 text-sm">
          {entries.map((e) => {
            const who = e.userId ? (userById.get(e.userId)?.name ?? 'unbekannt') : 'System'
            const sig = e.signatureId ? sigById.get(e.signatureId) : null
            const recipient = sig ? userById.get(sig.recipientUserId)?.name : null
            const label = describeAction(e.action, recipient, doc.uploaderUserId === e.userId)
            return (
              <li key={e.id} className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary">
                    <span className="font-medium">{who}</span> {label}
                  </p>
                  <p className="text-xs text-text-tertiary">
                    {new Date(e.createdAt).toLocaleString('de-DE')}
                  </p>
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

function describeAction(
  action: string,
  recipient: string | null | undefined,
  _isUploader: boolean,
): string {
  switch (action) {
    case 'uploaded':
      return 'hat das Dokument hochgeladen'
    case 'sent':
      return recipient ? `hat es an ${recipient} verschickt` : 'hat es verschickt'
    case 'opened':
      return 'hat es geöffnet'
    case 'signed':
      return 'hat unterzeichnet'
    case 'rejected':
      return 'hat abgelehnt'
    case 'archived':
      return 'hat es archiviert'
    case 'downloaded':
      return 'hat es heruntergeladen'
    default:
      return action
  }
}

export const documentDetailRoute = Route
